import React, { useEffect, useState } from 'react';

import dynamic from 'next/dynamic';

import { checkIfTvod } from 'src/utils/ConsumptionHelper/ConsumptionHelper';

import { useAppStore } from 'src/hooks/useAppStore';
import { getActiveSubscription } from '../../../services/apiRequests/SubscriptionService';
import { purchasePlan } from '../../../services/responseTypes/purchasePlanResponse';
import { SinglePlayResponse } from '../../../services/responseTypes/singlePlayResponse';
import { colors } from '../../../styles/colors';
import { ContentLiteData } from '../../../types/consumption';
import { getUserTokenFromLS } from '../../../utils/auth-utils/TokenHandler';
import { getLocalStorage } from '../../../utils/browserStorageHandler';
import { TVOD_JUST_BOUGHT } from '../../../utils/constants/localStorageConstants';
import {
  CountryCode,
  DisplayLanguage
} from '../../../utils/constants/stringConstants';
import { TAG_NAME } from '../../../utils/constants/TagNameConstants';
import {
  getPlanMatchingTier,
  getTVODPrice
} from '../../../utils/TvodHelper/tvodHelper';

// Lazy loading components
const BeforeStartPopup = dynamic(
  () => import('../../beforeStartPopup/BeforeStartPopup'),
  {
    ssr: false
  }
);
const Modal = dynamic(() => import('../../modal/Modal'), {
  ssr: false
});

const TvodRentalCallout = dynamic(
  () => import('../../tvodRentalCallOutPopUp'),
  {
    ssr: false
  }
);

const PlexRentNowPopUp = dynamic(
  () => import('../../plexRentNowPopUp/PlexRentNowPopUp'),
  {
    ssr: false
  }
);

interface TvodControllerProps {
  singlePlayDetailsMovie: SinglePlayResponse;
  contentLiteData: ContentLiteData;
  contentIdFromUrl: string;
  showPlexRentNowPopUp: boolean;
  setPlexRentNowPopUp: React.Dispatch<React.SetStateAction<boolean>>;
}
interface PlayerState {
  togglePlayer: boolean;
  triggerPlayer: boolean;
}

const TvodController: React.FC<TvodControllerProps> = ({
  singlePlayDetailsMovie,
  contentLiteData,
  contentIdFromUrl,
  showPlexRentNowPopUp,
  setPlexRentNowPopUp
}) => {
  const {
    tvodState,
    usersLocationData,
    initialLaunchData,
    usersPreferenceData,
    appConfig: { isRequestedByBot },
    translations,
    tvodRentals,
    comboOfferState,
    setTvodState,
    setComboOfferState
  } = useAppStore();
  const countrylist = initialLaunchData?.countrylist[0];
  const countryCode = usersLocationData?.country_code || CountryCode.IN;
  const displayLanguages =
    usersPreferenceData?.display_language || DisplayLanguage.ENGLISH;

  // Internal states and actions
  const [tvodPlanData, setTvodPlanData] = useState<any>();
  const [showBeforeStartPopUp, setBeforeStartPopUp] = useState(false);
  const [showTvodRentalCallout, setShowTvodRentalCallout] =
    useState<boolean>(false);

  const [playerState, setPlayerState] = useState<PlayerState>({
    togglePlayer: false,
    triggerPlayer: false
  });

  const getTvodPlanDetails = (tierName: string) => {
    if (tierName && tvodRentals?.tvodPlans) {
      const temp: purchasePlan[] = getPlanMatchingTier(
        tierName,
        tvodRentals.tvodPlans
      );
      temp[0] = { ...temp[0], price: price };
      setTvodPlanData(temp);
    }
  };

  const closeTvodRentalCallout = () => {
    setShowTvodRentalCallout(false);
  };

  const updatePlayerState = (playerState: PlayerState) => {
    setPlayerState((prevState) => ({ ...prevState, ...playerState }));
  };

  const handleTvodFlow = async () => {
    const userToken = getUserTokenFromLS();
    let currentPlan;
    if (userToken) {
      const subscriptionDataResponse = await getActiveSubscription(countryCode);
      if (subscriptionDataResponse) {
        currentPlan = subscriptionDataResponse.maxPricePack;
      }
    }

    const requiredParamsToCheckTvod = {
      singlePlayMovieData: singlePlayDetailsMovie,
      tvodRentals,
      z5StringsEn: translations.plex,
      countryCode,
      displayLanguage: displayLanguages,
      contentLanguages: contentLiteData.languages,
      state: usersLocationData?.state_code,
      currentActiveSubscriptions: currentPlan || [],
      movieId: contentIdFromUrl,
      countryList: countrylist
    };
    // check content is tvod
    checkIfTvod(
      requiredParamsToCheckTvod,
      setTvodState,
      updatePlayerState,
      setComboOfferState
    );
  };

  const { currency, price } = getTVODPrice(
    countrylist,
    singlePlayDetailsMovie?.assetDetails?.tier
  );

  // Handle lifecycle hooks
  useEffect(() => {
    const tier = singlePlayDetailsMovie?.assetDetails?.tier;
    if (tier) getTvodPlanDetails(tier);
  }, [singlePlayDetailsMovie?.assetDetails?.tier]);

  // check content is tvod
  useEffect(() => {
    if (
      singlePlayDetailsMovie &&
      !isRequestedByBot &&
      //tvodRentals?.allRentalPurchases?.length &&
      tvodRentals?.tvodPlans?.length
    ) {
      handleTvodFlow();
    }
  }, [singlePlayDetailsMovie?.assetDetails?.id, tvodRentals]);

  useEffect(() => {
    if (tvodState?.isTvodContent && !tvodState?.isTvodEvent) {
      const tvodJustBought = getLocalStorage(TVOD_JUST_BOUGHT);
      if (tvodJustBought && contentIdFromUrl === tvodJustBought) {
        setBeforeStartPopUp(true);
      }
    }
  }, [tvodState?.isTvodContent, tvodState?.isTvodEvent]);

  return (
    <>
      {showPlexRentNowPopUp ? (
        tvodState?.isTvodEvent ? (
          <Modal
            show={showTvodRentalCallout}
            onClose={closeTvodRentalCallout}
            width="450px"
            height="400px"
            background={colors.white}
            renderCloseIcon={true}
            separateCloseIcon={true}
            closeIconColor={colors.black}
            padding="0 16px 16px">
            <TvodRentalCallout
              singlePlayDetailsMovie={singlePlayDetailsMovie}
              tagName={TAG_NAME.PORTRAIT_SMALL}
              contentLiteData={contentLiteData}
              currency={currency}
              price={price}
              tvodPlanData={tvodPlanData}
            />
          </Modal>
        ) : (
          <PlexRentNowPopUp
            showPlexRentNowPopUp={showPlexRentNowPopUp}
            setPlexRentNowPopUp={setPlexRentNowPopUp}
            singlePlayDetailsMovie={
              singlePlayDetailsMovie as SinglePlayResponse
            }
            tvodPlanData={tvodPlanData}
            isComboOfferEnabled={comboOfferState.isComboOfferEnabled as boolean}
          />
        )
      ) : null}
      {showBeforeStartPopUp && (
        <BeforeStartPopup
          showBeforeStartPopUp={showBeforeStartPopUp}
          setBeforeStartPopUp={setBeforeStartPopUp}
          tvodPlanData={tvodPlanData}
          isComboOfferEnabled={comboOfferState.isComboOfferEnabled as boolean}
        />
      )}
    </>
  );
};

export default TvodController;
